import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from "react";
import { arrestDates } from "./ArrestDates";
import { exampleCases } from "./ExampleCases";

// Active case: circle-in → line-in → typing → text-out
// On text-out, old case hands off to the exit track (line-out → circle-out)
// while the new case starts circle-in simultaneously.
type ActivePhase = "circle-in" | "line-in" | "typing" | "text-out";
type ExitPhase = "text-out" | "line-out" | "circle-out";

const INTRO_CASES = exampleCases.filter((c) => c.section === "Intro");

const CIRCLE_IN_MS = 400;
const LINE_IN_MS = 600;
const CHAR_MS = 22.5; // ms per character typed
const HEAD_START_CHARS = Math.round(200 / CHAR_MS); // date types before description
const TEXT_HOLD_MS = 1000;
const TEXT_OUT_MS = 1000;
const LINE_OUT_MS = 400;
const CIRCLE_OUT_MS = 400;

// Intro reveal sequence timing
const AXIS_REVEAL_MS = 300; // when the axis line starts fading in
const CIRCLES_REVEAL_MS = 600; // when the circle cascade starts
const CIRCLE_STAGGER_MS = 10; // cascade delay per circle (left → right)
// Case anim starts once the full circle cascade has finished (400ms fade)
const CASE_ANIM_START_MS = 2500; // when the first case starts animating in — set to after the circle cascade finishes
const LABEL_REVEAL_MS = [1500, 2200, 2900]; // When each axis label fades in

export const HEADLINE_REVEAL_MS = 7500; // when the headline fades in
const DECK_REVEAL_MS = HEADLINE_REVEAL_MS + 500; // when the deck fades in

type Char = { char: string; highlighted: boolean };

const buildChars = (
  example: typeof exampleCases[0],
): { dateChars: Char[]; descChars: Char[] } => {
  const simplifiedDate = new Date(example.arrest_date).toLocaleDateString(
    "en-US",
    { month: "long", day: "numeric", year: "numeric" },
  );

  const dateChars: Char[] = simplifiedDate
    .toUpperCase()
    .split("")
    .map((char) => ({ char, highlighted: false }));

  if (!example.description.includes(example.bolded_part)) {
    throw new Error(
      `Description for ${example.name} does not contain the bolded_part!`,
    );
  }

  const descriptionParts = example.description.split(example.bolded_part);

  const parts: Array<{ text: string; highlighted: boolean }> = [
    { text: example.name.toUpperCase(), highlighted: true },
    { text: ", FROM ", highlighted: false },
    { text: example.origin_country.toUpperCase(), highlighted: true },
    { text: ", WAS ", highlighted: false },
    { text: descriptionParts[0].toUpperCase(), highlighted: false },
    { text: example.bolded_part.toUpperCase(), highlighted: true },
    { text: descriptionParts[1].toUpperCase(), highlighted: false },
  ];

  const descChars = parts.flatMap(({ text, highlighted }) =>
    text.split("").map((char) => ({ char, highlighted })),
  );

  return { dateChars, descChars };
};

const calculateDaysBetweenDates = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.floor(
    (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24),
  );
};

/**
 * Reads the CSS custom properties defined on the intro animation container
 * (--intro-circle-size, --intro-line-length, --intro-axis-position) and
 * re-reads them whenever the window is resized, so JS layout calculations
 * stay in sync with whatever SCSS breakpoint is active.
 */
const useIntroDimensions = (ref: React.RefObject<HTMLElement>) => {
  const [dims, setDims] = useState({
    circleSize: 45,
    lineLength: 30,
    axisPosition: 55,
  });

  // Read immediately after the DOM is painted so there's no flash of wrong values
  useLayoutEffect(() => {
    if (!ref.current) return;
    const s = getComputedStyle(ref.current);
    const n = (prop: string) => Number(s.getPropertyValue(prop).trim());
    setDims({
      circleSize: n("--intro-circle-size"),
      lineLength: n("--intro-line-length"),
      axisPosition: n("--intro-axis-position"),
    });
  }, [ref]);

  // Re-read on resize so values update when a breakpoint is crossed
  useEffect(() => {
    const read = () => {
      if (!ref.current) return;
      const s = getComputedStyle(ref.current);
      const n = (prop: string) => Number(s.getPropertyValue(prop).trim());
      setDims({
        circleSize: n("--intro-circle-size"),
        lineLength: n("--intro-line-length"),
        axisPosition: n("--intro-axis-position"),
      });
    };
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, [ref]);

  return dims;
};

export const IntroAnimation: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { circleSize, lineLength, axisPosition } =
    useIntroDimensions(containerRef);

  const timelineStart = "10/14/2025";
  const timelineEnd = "03/14/2026";
  const totalDays = calculateDaysBetweenDates(timelineStart, timelineEnd);

  const getHorizontalPosition = (days: number) => (days / totalDays) * 100;

  // Intro reveal sequence
  const [introStage, setIntroStage] = useState(0); // 0=blank, 1=axis, 2=circles, 3=running
  const [showHeadline, setShowHeadline] = useState(false);
  const [showDeck, setShowDeck] = useState(false);
  const [labelVisible, setLabelVisible] = useState<boolean[]>([
    false,
    false,
    false,
  ]);

  useEffect(() => {
    const t1 = setTimeout(() => setIntroStage(1), AXIS_REVEAL_MS);
    const t2 = setTimeout(() => setIntroStage(2), CIRCLES_REVEAL_MS);
    const t3 = setTimeout(() => setIntroStage(3), CASE_ANIM_START_MS);
    const t4 = setTimeout(() => setShowHeadline(true), HEADLINE_REVEAL_MS);
    const t5 = setTimeout(() => setShowDeck(true), DECK_REVEAL_MS);
    const labelTimeouts = LABEL_REVEAL_MS.map((delay, i) =>
      setTimeout(
        () =>
          setLabelVisible((prev) => {
            const next = [...prev];
            next[i] = true;
            return next;
          }),
        delay,
      ),
    );
    return () => [t1, t2, t3, t4, t5, ...labelTimeouts].forEach(clearTimeout);
  }, []);

  const [activeCaseIdx, setActiveCaseIdx] = useState(0);
  const [phase, setPhase] = useState<ActivePhase>("circle-in");
  // Single counter: date chars revealed at [0..], desc chars revealed at [HEAD_START_CHARS..]
  const [revealedChars, setRevealedChars] = useState(0);

  const [exitingCaseIdx, setExitingCaseIdx] = useState<number | null>(null);
  const [exitPhase, setExitPhase] = useState<ExitPhase | null>(null);
  const [exitingRevChars, setExitingRevChars] = useState(0);

  // Pre-build char arrays for all cases once
  const allCaseData = useMemo(() => INTRO_CASES.map(buildChars), []);
  const activeDescLength = allCaseData[activeCaseIdx].descChars.length;

  // Active case phase machine
  useEffect(() => {
    if (introStage < 3) return; // wait for the intro reveal to finish
    let timeout: ReturnType<typeof setTimeout>;

    switch (phase) {
      case "circle-in":
        timeout = setTimeout(() => setPhase("line-in"), CIRCLE_IN_MS);
        break;
      case "line-in":
        timeout = setTimeout(() => {
          setRevealedChars(0);
          setPhase("typing");
        }, LINE_IN_MS);
        break;
      case "typing":
        if (revealedChars >= HEAD_START_CHARS + activeDescLength) {
          timeout = setTimeout(() => setPhase("text-out"), TEXT_HOLD_MS);
        } else {
          timeout = setTimeout(
            () => setRevealedChars((prev) => prev + 1),
            CHAR_MS,
          );
        }
        break;
      case "text-out": {
        // Hand the old case to the exit track immediately (text-out phase keeps it visible
        // while it fades), then start the new case's circle-in at the same time.
        setExitingCaseIdx(activeCaseIdx);
        setExitingRevChars(revealedChars); // snapshot so the exiting text stays visible
        setExitPhase("text-out");
        setActiveCaseIdx((prev) => (prev + 1) % INTRO_CASES.length);
        setRevealedChars(0);
        setPhase("circle-in");
        break;
      }
    }

    return () => clearTimeout(timeout);
  }, [phase, revealedChars, activeDescLength, activeCaseIdx, introStage]);

  // Exit case phase machine — runs independently from the active case
  useEffect(() => {
    if (!exitPhase) return;
    let duration: number;
    if (exitPhase === "text-out") duration = TEXT_OUT_MS;
    else if (exitPhase === "line-out") duration = LINE_OUT_MS;
    else duration = CIRCLE_OUT_MS;
    const timeout = setTimeout(() => {
      if (exitPhase === "text-out") {
        setExitPhase("line-out");
      } else if (exitPhase === "line-out") {
        setExitPhase("circle-out");
      } else {
        setExitingCaseIdx(null);
        setExitPhase(null);
      }
    }, duration);
    return () => clearTimeout(timeout);
  }, [exitPhase]);

  const getCaseLayout = (caseIdx: number) => {
    const c = INTRO_CASES[caseIdx];
    const daysIn = calculateDaysBetweenDates(timelineStart, c.arrest_date);
    const hp = getHorizontalPosition(daysIn);
    const lineLeft = `calc(${hp}vw + ${circleSize / 2}px)`;
    const descStyle: React.CSSProperties =
      hp > 50
        ? {
            right: `calc(${100 - hp}vw - ${circleSize / 2}px + 15px)`,
            width: "600px",
          }
        : { left: `calc(${hp}vw + ${circleSize / 2}px + 15px)` };
    return { hp, lineLeft, descStyle, colorClass: `case-color-${caseIdx % 3}` };
  };

  const renderCase = (
    stableKey: string,
    caseIdx: number,
    phaseStr: string,
    revChars: number,
  ) => {
    const { hp, lineLeft, descStyle, colorClass } = getCaseLayout(caseIdx);
    const { dateChars, descChars } = allCaseData[caseIdx];

    return (
      <div key={stableKey} className={`example-case ${colorClass}`}>
        {/* Vertical connecting line */}
        <div
          className={`has-background-white example-case-line phase-${phaseStr}`}
          style={{
            position: "absolute",
            top: `calc(${axisPosition - lineLength}vh - ${
              circleSize / 2
            }px - 15px)`,
            left: lineLeft,
            height: `${lineLength}vh`,
            width: "1px",
            opacity: 0.5,
            transformOrigin: "bottom",
          }}
        />

        {/* Date eyebrow — types out first, ~500ms before description */}
        <div
          className={`example-case-date eyebrow phase-${phaseStr}`}
          style={{
            position: "absolute",
            top: `calc(${axisPosition - lineLength}vh - ${
              circleSize / 2
            }px - 80px)`,
            ...descStyle,
          }}
        >
          {dateChars.map((c, i) => (
            <span key={i} style={{ opacity: i < revChars ? 1 : 0 }}>
              {c.char}
            </span>
          ))}
        </div>

        {/* Case description — starts typing ~500ms after the date */}
        <div
          className={`example-case-description eyebrow phase-${phaseStr}`}
          style={{
            position: "absolute",
            top: `calc(${axisPosition - lineLength}vh - ${
              circleSize / 2
            }px - 50px)`,
            maxWidth: "600px",
            ...descStyle,
          }}
        >
          {descChars.map((c, i) => (
            <span
              key={i}
              className={c.highlighted ? "example-case-data" : undefined}
              style={{ opacity: i < revChars - HEAD_START_CHARS ? 1 : 0 }}
            >
              {c.char}
            </span>
          ))}
        </div>

        {/* Highlighted circle on the axis */}
        <div
          className={`example-case-circle phase-${phaseStr}`}
          style={{
            position: "absolute",
            left: `calc(${hp}vw)`,
            top: `calc(${axisPosition}vh - ${circleSize / 2}px)`,
            width: `${circleSize}px`,
            height: `${circleSize}px`,
            zIndex: 20,
            borderRadius: "100%",
          }}
        />
      </div>
    );
  };

  return (
    <div
      className="scrolly-container intro-animation-container"
      ref={containerRef}
    >
      <div className="hero is-fullheight is-flex is-flex-direction-column is-justify-content-flex-end">
        {/* Timeline axis */}
        <div
          className="has-background-white"
          style={{
            position: "absolute",
            top: `${axisPosition}vh`,
            width: "100%",
            height: "1px",
            opacity: introStage >= 1 ? 1 : 0,
            transition: "opacity 600ms ease",
          }}
        />

        {/* Circles on axis to represent arrests — cascade left to right */}
        {arrestDates.map((arrest, i) => {
          const pos = getHorizontalPosition(arrest.days_in);
          return (
            <div
              key={i}
              className="has-background-grey-light"
              style={{
                position: "absolute",
                left: `calc(${pos}vw)`,
                top: `calc(${axisPosition}vh - ${circleSize / 2}px)`,
                width: `${circleSize}px`,
                height: `${circleSize}px`,
                zIndex: 10,
                opacity:
                  introStage >= 2
                    ? ((arrest.number_of_arrests - 3) / 25) * 0.9
                    : 0,
                borderRadius: "100%",
                transition: `opacity 1500ms ease ${
                  Math.random() * arrestDates.length * CIRCLE_STAGGER_MS
                }ms`,
              }}
            />
          );
        })}

        {/* Exiting case — text-out fade, then line-out + circle-out */}
        {introStage >= 3 &&
          exitingCaseIdx !== null &&
          exitPhase !== null &&
          renderCase(
            `exit-${exitingCaseIdx}`,
            exitingCaseIdx,
            exitPhase,
            exitingRevChars,
          )}

        {/* Active (entering) case */}
        {introStage >= 3 &&
          renderCase(
            `active-${activeCaseIdx}`,
            activeCaseIdx,
            phase,
            revealedChars,
          )}

        {/* Axis labels — fade in as their section of circles comes in */}
        {["Nov '25", "Jan '26", "Mar '26"].map((label, i) => (
          <div
            key={i}
            className="eyebrow axis-labels has-text-centered"
            style={{
              position: "absolute",
              top: `calc(${axisPosition}vh + ${circleSize / 2}px + 10px)`,
              left: `calc(${i * 40 + 10}vw - 25px)`,
              opacity: labelVisible[i] ? 1 : 0,
              transition: "opacity 500ms ease",
            }}
          >
            {label}
          </div>
        ))}

        <div className="landing-content is-flex is-flex-direction-column is-align-items-center">
          <h1
            className="headline mb-0 has-text-centered"
            style={{
              opacity: showHeadline ? 1 : 0,
              transition: "opacity 5000ms ease",
            }}
          >
            We Found 430 ICE Street Arrests in the New York Area. More Than 93%
            Targeted Latinos.
          </h1>
          <div
            className="deck mt-3 mb-0 has-text-centered"
            style={{
              opacity: showDeck ? 1 : 0,
              transition: "opacity 5000ms ease",
            }}
          >
            An investigation by The City Reporter, based on a database of more
            than 1,200 lawsuits, shows that ICE agents in the field have seized
            Latinos in numbers that far exceed their share of the undocumented
            population.
          </div>
        </div>
      </div>
    </div>
  );
};
